/**
 * QuizReviewStage - 퀴즈 채점 결과 리뷰 화면
 *
 * 모듈 마무리 퀴즈 완료 후 "채점 결과 보기" 클릭 시 표시
 * QuizStage의 리뷰 모드와 동일하게 실제 문제 화면에서 정오답, 해설 표시
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    Loader2,
    ArrowLeft,
    CheckCircle,
    XCircle,
    Trophy,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { quizScriptApi } from '@/api/quizScript'
import { learningSessionApi, type QuizQuestionReview } from '@/api/learningSession'
import type {
    QuizScript,
    QuizSolveResult,
    MultiChoiceQuizData,
    QuizData,
} from '@quebon/shared-learning'
import {
    QuizRenderer,
    substituteVariables,
} from '@quebon/shared-learning'

// ============================================================================
// Types
// ============================================================================

export interface QuizReviewStageProps {
    sessionId: number
    onBack: () => void
}

// ============================================================================
// Component
// ============================================================================

export function QuizReviewStage({ sessionId, onBack }: QuizReviewStageProps) {
    const { t } = useTranslation('learner')

    // Fetch session review data
    const { data: review, isLoading: isLoadingReview, error } = useQuery({
        queryKey: ['quizSessionReview', sessionId],
        queryFn: () => learningSessionApi.getQuizSessionReview(sessionId),
        enabled: sessionId > 0,
        staleTime: 60 * 1000,
    })

    // State
    const [quizScripts, setQuizScripts] = useState<Map<string, QuizScript>>(new Map())
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isLoadingQuiz, setIsLoadingQuiz] = useState(false)

    // Quiz list from review data
    const quizItems = useMemo(() => review?.questions || [], [review])
    const currentQuizItem = quizItems[currentIndex]
    const currentQuizId = currentQuizItem?.quizId ?? ''
    const currentVars = currentQuizItem?.varInstantiation
    const currentCacheKey = currentVars ? `${currentQuizId}_${JSON.stringify(currentVars)}` : currentQuizId
    const currentQuizScript = quizScripts.get(currentCacheKey)

    // Load quiz script (변수 인스턴스화 적용)
    const loadQuizScript = useCallback(async (quizId: string, vars?: Record<string, unknown> | null) => {
        const cacheKey = vars ? `${quizId}_${JSON.stringify(vars)}` : quizId
        if (quizScripts.has(cacheKey)) return quizScripts.get(cacheKey)!

        setIsLoadingQuiz(true)
        try {
            const script = await quizScriptApi.getQuiz(quizId, false, vars ?? undefined)
            setQuizScripts(prev => new Map(prev).set(cacheKey, script))
            return script
        } catch (err) {
            console.error('Failed to load quiz script:', err)
            return null
        } finally {
            setIsLoadingQuiz(false)
        }
    }, [quizScripts])

    // Load current quiz when index changes (변수 인스턴스화 전달)
    useEffect(() => {
        if (currentQuizId) {
            loadQuizScript(currentQuizId, currentVars)
        }
    }, [currentQuizId, currentVars, loadQuizScript])

    // Convert QuizScript to legacy QuizData format
    const getLegacyQuizData = useCallback((quizScript: QuizScript): QuizData | null => {
        if (!quizScript) return null

        const rawData = quizScript as unknown as Record<string, unknown>
        const qd = (rawData.quizData as QuizData) || quizScript

        return {
            ...qd,
            type: quizScript.type || qd.type,
            title: quizScript.title || qd.title,
            problem: quizScript.problem || qd.problem,
            vars: quizScript.vars || qd.vars,
            image: quizScript.image || qd.image,
            imageMobile: quizScript.imageMobile || qd.imageMobile,
        } as QuizData
    }, [])

    // Build solveResult from review data for display
    const buildSolveResult = useCallback((question: QuizQuestionReview): QuizSolveResult => {
        // explanation text에 변수 치환 적용
        const vars = question.varInstantiation
        const hasVars = vars && Object.keys(vars).length > 0
        const rawText = question.explanation?.text
        const substitutedText = (rawText && hasVars)
            ? substituteVariables(rawText, vars as Record<string, string | number>)
            : rawText

        return {
            correct: question.isCorrect,
            corrects: question.corrects || [],
            correctAnswers: (question.correctAnswer || []).map(a => String(a)),
            elapsedTime: 0,
            description: question.explanation ? {
                text: substitutedText ?? undefined,
                image: question.explanation.image ? {
                    signedUrl: question.explanation.image.signedUrl || question.explanation.image.url || undefined,
                    scaleX: question.explanation.image.scaleX ?? undefined,
                    scaleY: question.explanation.image.scaleY ?? undefined,
                } : undefined,
                imageMobile: question.explanation.imageMobile ? {
                    signedUrl: question.explanation.imageMobile.signedUrl || question.explanation.imageMobile.url || undefined,
                    scaleX: question.explanation.imageMobile.scaleX ?? undefined,
                    scaleY: question.explanation.imageMobile.scaleY ?? undefined,
                } : undefined,
                desmos: question.explanation.desmos ?? undefined,
            } : undefined,
        }
    }, [])

    // Convert stored answers back to display format
    const getDisplayAnswers = useCallback((
        quizScript: QuizScript,
        userAnswer: unknown[] | null
    ): string[] => {
        if (!userAnswer || userAnswer.length === 0) return []
        const legacyQuiz = getLegacyQuizData(quizScript)
        if (!legacyQuiz) return userAnswer.map(a => String(a))

        switch (legacyQuiz.type) {
            case 'multi_choice': {
                // 제출 시 choice key → 1-indexed 위치값으로 변환되어 저장됨
                // 리뷰 시 역변환 필요: 위치값 → choice key
                const quiz = legacyQuiz as MultiChoiceQuizData
                const allKeys = quiz.choices.map(c => c.key)
                return userAnswer.map(a => {
                    const str = String(a)
                    if (allKeys.includes(str)) return str
                    const posIdx = typeof a === 'number' ? a : parseInt(str, 10)
                    if (!isNaN(posIdx) && posIdx >= 1 && posIdx <= quiz.choices.length) {
                        return quiz.choices[posIdx - 1].key
                    }
                    return str
                })
            }
            case 'boolean':
            case 'select_text':
            case 'fill_blank':
            case 'drag_text':
            default:
                return userAnswer.map(a => String(a))
        }
    }, [getLegacyQuizData])

    // Navigation
    const handlePrevious = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1)
        }
    }, [currentIndex])

    const handleNext = useCallback(() => {
        if (currentIndex < quizItems.length - 1) {
            setCurrentIndex(currentIndex + 1)
        }
    }, [currentIndex, quizItems.length])

    const handleNavClick = useCallback((index: number) => {
        if (index !== currentIndex) {
            setCurrentIndex(index)
        }
    }, [currentIndex])

    // Calculate score
    const calculateScore = useMemo(() => {
        if (!review) return { total: 0, correct: 0, percentage: 0 }
        return {
            total: review.totalQuestions,
            correct: review.correctCount,
            percentage: review.score,
        }
    }, [review])

    // Get legacy quiz data for rendering
    const legacyQuiz = currentQuizScript ? getLegacyQuizData(currentQuizScript) : null
    const currentSolveResult = currentQuizItem ? buildSolveResult(currentQuizItem) : undefined
    const currentDisplayAnswers = currentQuizScript && currentQuizItem
        ? getDisplayAnswers(currentQuizScript, currentQuizItem.userAnswer)
        : undefined
    // ========================================================================
    // Render: Loading state
    // ========================================================================
    if (isLoadingReview) {
        return (
            <div className="flex flex-col justify-center items-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
                <p className="mt-4 text-gray-600">{t('learning.quizReview.loadingResults')}</p>
            </div>
        )
    }

    // ========================================================================
    // Render: Error state
    // ========================================================================
    if (error || !review) {
        return (
            <div className="flex flex-col justify-center items-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <p className="mb-4 text-gray-500">{t('learning.quizReview.cannotLoadResults')}</p>
                <button
                    onClick={onBack}
                    className="flex gap-2 items-center text-indigo-600 hover:text-indigo-800"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('learning.quizReview.goBack')}
                </button>
            </div>
        )
    }

    // ========================================================================
    // Render: No quizzes
    // ========================================================================
    if (quizItems.length === 0) {
        return (
            <div className="flex flex-col justify-center items-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <p className="mb-4 text-gray-500">{t('learning.quizReview.noResults')}</p>
                <button
                    onClick={onBack}
                    className="flex gap-2 items-center text-indigo-600 hover:text-indigo-800"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('learning.quizReview.goBack')}
                </button>
            </div>
        )
    }

    // ========================================================================
    // Render: Main Review UI
    // ========================================================================
    return (
        <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600">
                <div className="flex justify-between items-center">
                    <div>
                        <button
                            onClick={onBack}
                            className="flex gap-2 items-center mb-2 text-sm text-indigo-200 transition-colors hover:text-white"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {t('learning.quizReview.goBack')}
                        </button>
                        <h2 className="text-xl font-bold text-white">{t('learning.quizReview.title')}</h2>
                        <p className="text-sm text-indigo-200">
                            {review.passed ? t('learning.quizReview.passed') : t('learning.quizReview.failed')} · {t('learning.quizReview.accuracy', { percentage: calculateScore.percentage })}
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold text-white">
                            {currentIndex + 1} / {quizItems.length}
                        </p>
                    </div>
                </div>
            </div>

            {/* Quiz Navigation */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex overflow-x-auto gap-2 p-1 custom-scrollbar">
                    {quizItems.map((quiz, index) => {
                        const isCurrent = index === currentIndex
                        const isCorrect = quiz.isCorrect

                        return (
                            <button
                                key={quiz.quizId}
                                onClick={() => handleNavClick(index)}
                                className={cn(
                                    'flex-shrink-0 w-10 h-10 rounded-lg font-medium text-sm transition-all flex items-center justify-center',
                                    isCurrent && 'ring-2 ring-indigo-500 ring-offset-2',
                                    isCorrect ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'
                                )}
                            >
                                {isCorrect ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Quiz Content */}
            <div className="p-6">
                {isLoadingQuiz ? (
                    <div className="flex flex-col justify-center items-center py-12">
                        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                        <p className="mt-3 text-gray-500">{t('learning.quizReview.loadingQuestion')}</p>
                    </div>
                ) : legacyQuiz ? (
                    <div className="mx-auto max-w-3xl">
                        {/* Result badge */}
                        <div className={cn(
                            'flex items-center gap-2 mb-4 px-4 py-2 rounded-lg w-fit',
                            currentQuizItem?.isCorrect ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                        )}>
                            {currentQuizItem?.isCorrect ? (
                                <>
                                    <CheckCircle className="w-5 h-5" />
                                    <span className="font-semibold">{t('learning.quizReview.correct')}</span>
                                </>
                            ) : (
                                <>
                                    <XCircle className="w-5 h-5" />
                                    <span className="font-semibold">{t('learning.quizReview.incorrect')}</span>
                                </>
                            )}
                        </div>

                        <QuizRenderer
                            key={`${currentQuizId}-review`}
                            quizType={legacyQuiz.type}
                            quizId={currentQuizId}
                            quiz={legacyQuiz}
                            onSolve={() => {}}
                            solveResult={currentSolveResult}
                            showReallyTrue={false}
                            useKeypad={false}
                            initialAnswers={currentDisplayAnswers}
                            hideSubmitButton={true}
                            disableSound={true}
                        />
                    </div>
                ) : (
                    <div className="flex flex-col justify-center items-center py-12">
                        <p className="text-gray-500">{t('learning.quizReview.cannotLoadData')}</p>
                    </div>
                )}
            </div>

            {/* Footer Navigation */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-between items-center mx-auto max-w-3xl">
                    <button
                        onClick={handlePrevious}
                        disabled={currentIndex === 0}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                            currentIndex === 0
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-700 hover:bg-gray-100'
                        )}
                    >
                        <ChevronLeft className="w-5 h-5" />
                        {t('learning.quizReview.previous')}
                    </button>

                    <span className="text-sm text-gray-500">
                        {currentIndex + 1} / {quizItems.length}
                    </span>

                    {currentIndex < quizItems.length - 1 ? (
                        <button
                            onClick={handleNext}
                            className="flex gap-2 items-center px-4 py-2 font-medium text-indigo-600 rounded-lg transition-colors hover:bg-indigo-50"
                        >
                            {t('learning.quizReview.next')}
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    ) : (
                        <button
                            onClick={onBack}
                            className="flex gap-2 items-center px-6 py-2 font-medium text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700"
                        >
                            {t('learning.quizReview.done')}
                            <Trophy className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default QuizReviewStage
